import { useQuery } from "@tanstack/react-query"
import { useApi } from "./api"

export enum ApiKeyEnvironment {
  TEST = "TEST",
  LIVE = "LIVE",
}

export enum ApiKeyScope {
  CHECKOUT = "CHECKOUT",
  READ_ONLY = "READ_ONLY",
  FULL = "FULL",
}

export enum ApiKeyStatus {
  ACTIVE = "ACTIVE",
  REVOKED = "REVOKED",
}

export interface ApiKey {
  id: string
  label: string
  env: ApiKeyEnvironment
  scope: ApiKeyScope
  prefix: string
  status: ApiKeyStatus
  lastUsedAt: string | null
  createdAt: string
  revokedAt: string | null
  rotatedFromId: string | null
}

export interface ApiKeyWithSecret extends ApiKey {
  secret: string
  publishableKey: string
}

export interface CreateApiKeyRequest {
  label: string
  env: ApiKeyEnvironment
  scope: ApiKeyScope
}

export interface UpdateApiKeyRequest {
  label: string
}

function generateIdempotencyKey() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID()
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

export function useApiKeys() {
  const { get } = useApi()

  const query = useQuery({
    queryKey: ["api-keys"],
    queryFn: () => get("api-keys"),
  })

  return {
    apiKeys: query.data,
    error: query.error,
    isLoading: query.isLoading,
    mutate: query.refetch,
  }
}

export function useApiKeysActions() {
  const { post, remove, patch } = useApi()

  return {
    async create(request: CreateApiKeyRequest) {
      const idempotencyKey = generateIdempotencyKey()
      return post("api-keys", request, {
        headers: { "Idempotency-Key": idempotencyKey },
      }) as Promise<ApiKeyWithSecret>
    },
    async rotate(apiKeyId: string) {
      const idempotencyKey = generateIdempotencyKey()
      return post(`api-keys/${apiKeyId}/rotate`, {}, {
        headers: { "Idempotency-Key": idempotencyKey },
      }) as Promise<ApiKeyWithSecret>
    },
    async revoke(apiKeyId: string) {
      return remove(`api-keys/${apiKeyId}`) as Promise<ApiKey>
    },
    async rename(apiKeyId: string, request: UpdateApiKeyRequest) {
      return patch(`api-keys/${apiKeyId}`, request) as Promise<ApiKey>
    },
  }
}
